import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface part {
  titre: string;
  numberOfQuestions: number;
  numberOfAnswers: number;
}

interface Template {
  id: number;
  title: string;
  total_questions: number;
  default_answers: number;
  user_id: number | null;
  created_at: string;
  updated_at: string;
}

const CreateTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [testTitle, setTestTitle] = useState<string>('');
  const [parts, setparts] = useState<part[]>([]);
  const [newpartTitre, setNewpartTitre] = useState<string>('');
  const [newpartQuestions, setNewpartQuestions] = useState<string>('');
  const [newpartAnswers, setNewpartAnswers] = useState<string>('4');

  // Nouveaux états pour l'édition
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  // Charger la liste des templates au montage du composant
  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/my-templates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Échec du chargement des templates');
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = async (templateId: string): Promise<void> => {
    if (templateId === '') {
      // Réinitialiser en mode création
      setIsEditMode(false);
      setSelectedTemplateId('');
      setTestTitle('');
      setparts([]);
      setIsPublic(false);
      return;
    }

    // Charger les données du template
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Échec du chargement du template');
      }

      const data = await response.json();

      // Passer en mode édition
      setIsEditMode(true);
      setSelectedTemplateId(templateId);
      setTestTitle(data.template.title);
      setIsPublic(data.template.user_id === null); // Public si user_id est NULL

      // Reconstruire les parts à partir des données du template
      const loadedParts = reconstructPartsFromTemplate(data.parts, data.questions);
      setparts(loadedParts);

    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      alert('Échec du chargement du template');
    }
  };

  const reconstructPartsFromTemplate = (templateParts: any[], templateQuestions: any[]): part[] => {
    // Grouper les questions par part_id
    const partsMap = new Map<number | null, any[]>();

    templateParts.forEach(part => {
      partsMap.set(part.id, []);
    });

    templateQuestions.forEach(question => {
      if (question.part_id && partsMap.has(question.part_id)) {
        partsMap.get(question.part_id)!.push(question);
      }
    });

    // Convertir au format part
    return templateParts.map(part => {
      const questions = partsMap.get(part.id) || [];

      // Trouver le nombre de réponses le plus courant dans cette part
      const answerCounts: { [key: number]: number } = {};
      questions.forEach(q => {
        answerCounts[q.number_of_answers] = (answerCounts[q.number_of_answers] || 0) + 1;
      });

      const mostCommonAnswers = questions.length > 0
        ? parseInt(Object.keys(answerCounts).reduce((a, b) =>
            answerCounts[parseInt(a)] > answerCounts[parseInt(b)] ? a : b
          ))
        : 4;

      return {
        titre: part.titre,
        numberOfQuestions: questions.length,
        numberOfAnswers: mostCommonAnswers
      };
    });
  };

  // Vérification en temps réel que les valeurs sont des entiers valides
  const isValidInteger = (value: string): boolean => {
    const num = parseInt(value, 10);
    return !isNaN(num) && Number.isInteger(num) && num > 0 && value === num.toString();
  };

  const isValidAnswers = (value: string): boolean => {
    if (value === '') return false;
    const num = parseInt(value, 10);
    return isValidInteger(value) && num >= 2 && num <= 10;
  };

  // Validation des champs de nouvelle part
  const isNewpartValid = useMemo(() => {
    return (
      newpartTitre.trim() !== '' &&
      newpartQuestions !== '' && isValidInteger(newpartQuestions) &&
      newpartAnswers !== '' && isValidAnswers(newpartAnswers)
    );
  }, [newpartTitre, newpartQuestions, newpartAnswers]);

  // Gestion des parts
  const handleAddpart = (): void => {
    if (isNewpartValid) {
      setparts([...parts, {
        titre: newpartTitre.trim(),
        numberOfQuestions: parseInt(newpartQuestions, 10),
        numberOfAnswers: parseInt(newpartAnswers, 10)
      }]);
      setNewpartTitre('');
      setNewpartQuestions('');
      setNewpartAnswers('4');
    }
  };

  const handleRemovepart = (index: number): void => {
    setparts(parts.filter((_, i) => i !== index));
  };

  // Calculer le nombre total de questions
  const totalQuestions = useMemo(() => {
    return parts.reduce((sum, part) => sum + part.numberOfQuestions, 0);
  }, [parts]);

  // Le formulaire est valide si le titre est rempli et qu'il y a au moins une part
  const isFormValid = useMemo(() => {
    return testTitle.trim() !== '' && parts.length > 0;
  }, [testTitle, parts]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!isFormValid) return;

    // Passer les données à ConfigureTemplate avec le mode et isPublic
    navigate('/configure-template', {
      state: {
        testTitle,
        parts: parts,
        isEditMode,
        templateId: isEditMode ? selectedTemplateId : null,
        isPublic
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('templates.create.title')}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
              <FileText className="text-white w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800">{t('templates.create.newTemplate')}</h2>
              <p className="text-gray-600">{t('templates.create.subtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sélection de template existant */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <label htmlFor="templateSelect" className="block text-sm font-medium text-gray-700 mb-2">
                {isEditMode ? t('templates.edit.selectTemplate') : t('templates.create.selectExisting')}
              </label>
              <select
                id="templateSelect"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={loadingTemplates}
              >
                <option value="">{t('templates.create.newTemplate')}</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.title}
                    {template.user_id === null && ' 🌐 (Public)'}
                    {template.user_id !== null && ' 🔒 (Privé)'}
                    {' '}({template.total_questions} questions)
                  </option>
                ))}
              </select>
            </div>

            {/* Titre du test */}
            <div>
              <label htmlFor="testTitle" className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.create.testTitle')}
              </label>
              <input
                type="text"
                id="testTitle"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('templates.create.enterTitle')}
                required
              />
            </div>

            {/* Toggle Public/Privé */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {t('templates.create.makePublic')}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-2 ml-8">
                {isPublic
                  ? t('templates.create.publicDescription')
                  : t('templates.create.privateDescription')
                }
              </p>
              {isPublic && (
                <p className="text-xs text-orange-600 mt-2 ml-8">
                  ⚠️ {t('templates.create.publicWarning')}
                </p>
              )}
            </div>

            {/* Section des parts */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.create.parts')}
              </label>
              <p className="text-sm text-gray-500 mb-4">
                {t('templates.create.partsDescription')}
              </p>

              {/* Liste des parts ajoutées */}
              {parts.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="bg-gray-100 px-4 py-2 rounded-lg font-semibold text-sm">
                    {t('templates.create.totalQuestions')}: {totalQuestions} {totalQuestions > 1 ? t('templates.create.questionsPlural') : t('templates.create.question')}
                  </div>
                  {parts.map((part, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-lg"
                    >
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium block">
                          {t('templates.create.part')} {index + 1}: {part.titre}
                        </span>
                        <span className="text-sm text-gray-600">
                          {part.numberOfQuestions} {part.numberOfQuestions > 1 ? t('templates.create.questionsPlural') : t('templates.create.question')} • {part.numberOfAnswers} {part.numberOfAnswers > 1 ? t('templates.create.answersPlural') : t('templates.create.answer')} {t('templates.create.possible')}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovepart(index)}
                        className="text-red-600 hover:text-red-800 transition ml-4"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Formulaire d'ajout de part */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('templates.create.partTitle')}
                  </label>
                  <input
                    type="text"
                    value={newpartTitre}
                    onChange={(e) => setNewpartTitre(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={t('templates.create.partTitlePlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('templates.create.numberOfQuestions')}
                    </label>
                    <input
                      type="text"
                      value={newpartQuestions}
                      onChange={(e) => setNewpartQuestions(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                        newpartQuestions === '' || isValidInteger(newpartQuestions)
                          ? 'border-gray-300 focus:ring-blue-500'
                          : 'border-red-500 focus:ring-red-500'
                      }`}
                      placeholder={t('templates.create.numberOfQuestionsPlaceholder')}
                    />
                    {newpartQuestions !== '' && !isValidInteger(newpartQuestions) && (
                      <p className="mt-1 text-xs text-red-600">{t('templates.create.validationIntegerPositive')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('templates.create.possibleAnswers')}
                    </label>
                    <input
                      type="text"
                      value={newpartAnswers}
                      onChange={(e) => setNewpartAnswers(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                        newpartAnswers === '' || isValidAnswers(newpartAnswers)
                          ? 'border-gray-300 focus:ring-blue-500'
                          : 'border-red-500 focus:ring-red-500'
                      }`}
                      placeholder={t('templates.create.possibleAnswersPlaceholder')}
                    />
                    {newpartAnswers !== '' && !isValidAnswers(newpartAnswers) && (
                      <p className="mt-1 text-xs text-red-600">{t('templates.create.validationBetween2And10')}</p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddpart}
                  disabled={!isNewpartValid}
                  className={`w-full py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm ${
                    isNewpartValid
                      ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Ajouter cette part
                </button>
              </div>
            </div>

            {/* Bouton d'action */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={!isFormValid}
                className={`px-8 py-3 rounded-lg font-semibold transition ${
                  isFormValid
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isEditMode ? t('templates.edit.continue') : 'Configurer le template'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateTemplate;
